
// PointViews Hjælp-panel. Renders the data-driven model in help/helpModel.js
// as two views INSIDE the SideRail slide-out (no separate modal any more):
//
//   • forside  - kort intro, søgefelt, kategorier med klikbare artikler
//   • artikel  - titel, afsnit, "Relaterede værktøjer", tilbage-navigation
//
// mountDigitizationHelpPanel(container) returns a handle:
//   { showHome(), showArticle(id), destroy() }
// sidebar.js keeps the current handle and exposes viewer.digitizationHelp.open(id)
// so any other UI (a tool's info-label, a context-menu entry, an error
// message) can deep-link straight to one article via a stable id.
//
// Design constraints honoured here (Hjælp-system task):
//   §5  PointView dark look, ~440px panel, reuses SideRail - not a webapp.
//   §16 article view keeps the panel open, scrolls on its own, has clear
//       back-navigation; related links open the next article in place -
//       the user is never bounced back to the forside between articles.
//   §17 opening/navigating Hjælp never touches tool state, selection,
//       camera or geometry - this module only builds/removes DOM inside
//       the panel body and adds NO document-level key handlers (the old
//       modal's Escape handler, which could swallow a tool's own ESC, is
//       gone).

import {HELP_CATEGORIES, HELP_ARTICLES, getArticle, articlesInCategory, categoryLabel, searchArticles, validateHelpModel} from "./help/helpModel.js";

import {t} from "./i18n/index.js";
// One-time dev sanity check - a broken related-id or orphan category shows
// up in the console instead of as a dead link in the panel.
try {
	const check = validateHelpModel();
	if(!check.ok) console.warn("[HelpPanel] helpModel issues:\n" + check.errors.join("\n"));
} catch(e){ /* never let a help self-check break the viewer */ }

const el = (tag, css, text) => {
	const n = document.createElement(tag);
	if(css) n.style.cssText = css;
	if(text != null) n.textContent = text;
	return n;
};

export function mountDigitizationHelpPanel(container){
	// Accept either a raw DOM element or a jQuery-wrapped one (kept for
	// backwards-compatibility with the previous sidebar call site).
	const root = (container && container.jquery) ? container[0] : container;
	root.innerHTML = "";
	root.classList.add("pv-help");

	const homeEl = el("div", "display:block;");
	const articleEl = el("div", "display:none;");
	root.append(homeEl, articleEl);

	// history of visited article ids, for the back button (§16)
	const history = [];

	// ── forside ────────────────────────────────────────────────────────
	const intro = el("p", null, t("Vælg et emne, eller søg efter et værktøj eller et begreb."));
	intro.className = "pv-help-intro";

	const search = el("input");
	search.type = "search";
	search.placeholder = t("Søg i hjælpen…");
	search.className = "pv-help-search";
	search.setAttribute("aria-label", t("Søg i hjælpen"));

	const categoriesWrap = el("div");
	const resultsWrap = el("div", "display:none;");

	homeEl.append(intro, search, categoriesWrap, resultsWrap);

	function articleRow(article){
		const row = el("button", null);
		row.className = "pv-help-row";
		row.type = "button";
		const label = el("span", null, article.title);
		const chev = el("span", null, "›");
		chev.className = "pv-help-chev";
		row.append(label, chev);
		row.addEventListener("click", () => openArticle(article.id, {fromHome: true}));
		return row;
	}

	function renderCategories(){
		categoriesWrap.innerHTML = "";
		for(const cat of HELP_CATEGORIES){
			const items = articlesInCategory(cat.id);
			if(items.length === 0) continue;
			const section = el("div");
			section.className = "pv-help-cat";
			section.append(el("div", null, cat.label));
			section.lastChild.className = "pv-help-cat-title";
			for(const a of items) section.append(articleRow(a));
			categoriesWrap.append(section);
		}
	}

	function renderResults(query){
		const hits = searchArticles(query);
		resultsWrap.innerHTML = "";
		if(hits.length === 0){
			resultsWrap.append(el("p", null, t("Ingen emner matcher “{0}”.", query)));
			resultsWrap.lastChild.className = "pv-help-intro";
			return;
		}
		const head = el("div", null, hits.length === 1 ? t("1 emne") : t("{0} emner", hits.length));
		head.className = "pv-help-cat-title";
		resultsWrap.append(head);
		for(const a of hits){
			const row = articleRow(a);
			const sub = el("span", null, a.summary);
			sub.className = "pv-help-row-sub";
			row.insertBefore(sub, row.querySelector(".pv-help-chev"));
			row.classList.add("pv-help-row--rich");
			resultsWrap.append(row);
		}
	}

	search.addEventListener("input", () => {
		const q = search.value.trim();
		if(q.length === 0){
			resultsWrap.style.display = "none";
			categoriesWrap.style.display = "";
			resultsWrap.innerHTML = "";
			return;
		}
		categoriesWrap.style.display = "none";
		resultsWrap.style.display = "";
		renderResults(q);
	});

	// ── artikel ────────────────────────────────────────────────────────
	function renderArticle(article){
		articleEl.innerHTML = "";

		const back = el("button", null, history.length > 1 ? t("‹ Tilbage") : t("‹ Alle emner"));
		back.type = "button";
		back.className = "pv-help-back";
		back.addEventListener("click", () => {
			history.pop(); // current
			const prev = history[history.length - 1];
			if(prev){ history.pop(); openArticle(prev); }
			else showHome();
		});
		articleEl.append(back);

		const kicker = el("div", null, categoryLabel(article.category));
		kicker.className = "pv-help-kicker";
		articleEl.append(kicker);

		const h = el("h2", null, article.title);
		h.className = "pv-help-title";
		articleEl.append(h);

		const sum = el("p", null, article.summary);
		sum.className = "pv-help-summary";
		articleEl.append(sum);

		for(const section of article.sections){
			if(section.heading){
				const sh = el("h3", null, section.heading);
				sh.className = "pv-help-section-h";
				articleEl.append(sh);
			}
			const body = el("div");
			body.className = "pv-help-section";
			body.innerHTML = section.html;
			articleEl.append(body);
		}

		const related = (article.related || []).map(getArticle).filter(Boolean);
		if(related.length){
			const rh = el("h3", null, t("Relaterede værktøjer"));
			rh.className = "pv-help-section-h";
			articleEl.append(rh);
			const list = el("div");
			list.className = "pv-help-related";
			for(const r of related){
				const link = el("button", null, r.title);
				link.type = "button";
				link.className = "pv-help-related-link";
				link.addEventListener("click", () => openArticle(r.id));
				list.append(link);
			}
			articleEl.append(list);
		}

		root.scrollTop = 0;
		if(root.parentElement) root.parentElement.scrollTop = 0;
	}

	function openArticle(id, {fromHome = false} = {}){
		const article = getArticle(id);
		if(!article){
			// §14: unknown id must be handled safely - fall back to the forside
			console.warn(`[HelpPanel] ukendt hjælp-id: "${id}"`);
			showHome();
			return;
		}
		if(fromHome) history.length = 0;
		history.push(id);
		homeEl.style.display = "none";
		articleEl.style.display = "block";
		renderArticle(article);
	}

	function showHome(){
		history.length = 0;
		articleEl.style.display = "none";
		articleEl.innerHTML = "";
		homeEl.style.display = "block";
		search.value = "";
		resultsWrap.style.display = "none";
		resultsWrap.innerHTML = "";
		categoriesWrap.style.display = "";
	}

	renderCategories();
	showHome();

	return {
		showHome,
		showArticle: (id) => openArticle(id, {fromHome: true}),
		destroy: () => { root.innerHTML = ""; root.classList.remove("pv-help"); },
	};
}

// Kept as a named export so tests / other modules can enumerate what the
// panel can show without importing the model directly.
export const HELP_ARTICLE_IDS = HELP_ARTICLES.map((a) => a.id);
